import { useContext } from 'react';
import { useNavigate } from 'react-router';

import { faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, BoxProps } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { AnalyticsContext, ObjectTypes } from 'src/AnalyticsContext';
import Resident, { ResidentProps } from 'src/components/fragments/Resident';
import { useCareAssignments } from 'src/contexts/CareAssignmentsContext';
import useCareZones from 'src/hooks/useCareZones';
import { ResidentWithLatestActivity } from 'src/hooks/useOverdueRoutineChecks';
import { validateHospiceStatus } from 'src/utils/HospiceUtils';

interface ResidentCardProps extends ResidentProps {
    containerProps?: BoxProps;
    index?: number;
    hideArrow?: boolean;
    hideBadge?: boolean;
    onResidentClick?: () => void;
    preventDefault?: boolean;
    resident: ResidentWithLatestActivity;
    unitIsCheckedIn?: boolean;
}

export default function ResidentCard(props: ResidentCardProps) {
    const {
        containerProps = {},
        hideArrow = false,
        hideBadge = false,
        index,
        onResidentClick,
        preventDefault = false,
        progress,
        resident,
        unitIsCheckedIn,
    } = props;

    const { trackEvent } = useContext(AnalyticsContext);
    const navigate = useNavigate();
    const { shape } = useTheme();

    const { careZones } = useCareZones();
    const { pinnedResidentIds } = useCareAssignments();

    const defaultHandleOnClick = () => {
        navigate(`/residents/resident/${resident?.id}`);
    };

    const handleOnClick = () => {
        trackEvent('Resident Card Clicked', { objectType: ObjectTypes.Card, cardIndex: index });
        if (typeof onResidentClick === 'function') {
            onResidentClick();
        } else if (!preventDefault) {
            defaultHandleOnClick();
        }
    };

    const isPinned = pinnedResidentIds.has(resident.id!);

    return (
        <Box
            {...containerProps}
            onClick={handleOnClick}
            data-dd-action-name="Resident Card"
            data-testid="resident-card"
            sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'nowrap',
                cursor: 'pointer',
                bgcolor: 'common.white',
                borderRadius: shape.borderRadiusXl,
                p: 2,
                gap: 2,
            }}
        >
            <Resident
                resident={resident}
                unit={resident.unit}
                showCareType={(careZones?.length || 0) > 0}
                hospiceStatus={validateHospiceStatus(
                    resident.healthInfo?.hospiceHealthInfo?.hospiceStatus
                )}
                progress={progress}
                // Badge props
                isArchived={hideBadge ? false : resident.isArchived}
                isCheckedIn={hideBadge ? false : unitIsCheckedIn}
                isOnHold={hideBadge ? false : resident.isOnHold}
                isPinned={hideBadge ? false : isPinned}
            />
            {!hideArrow && <FontAwesomeIcon icon={faChevronRight} size="lg" />}
        </Box>
    );
}
